import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import SnoozeIcon from "@mui/icons-material/Snooze";
import moment from "moment";
import React, { useContext } from "react";
import { useHistory } from "react-router-dom";
import { CedarShellSidenavContext } from "../../../components/Layouts/CedarShell/CedarShellSidenavContext";
import { useDashboardRegistry } from "../../../hooks/react-query/dashboards/useDashboardRegistry";
import { useDismissAlert } from "../../../hooks/react-query/alerts/useDismissAlert";
import { AlertItem } from "../alertsMockData";
import { AlertSnoozeMenu } from "./AlertSnoozeMenu";

interface Props {
  alert: AlertItem;
}

// alert.dashboard_path can carry its own query string baked in (e.g.
// "/digital_performance?brand=Nissan&market=United+States" - filtering straight to the market/
// brand this alert is actually about). history.push's OBJECT form (unlike its string form, which
// parses "/path?query" itself) takes whatever `pathname` it's handed as-is - so passing the raw
// path straight through as `pathname` with `search` hardcoded to "" left the query string glued
// onto pathname, unparsed. Every exact-matched route (PrivateRoute) then failed to match that
// polluted pathname and silently fell through to the catch-all Home route instead of the intended
// dashboard - even though pasting the exact same URL and reloading works fine, since a full page
// load lets the browser's own URL parser split pathname/search correctly before React Router ever
// runs. Splitting it out here fixes both the navigation itself and the dashboard-name lookup just
// below, which had the same bug (comparing the registry's bare paths against a path that still had
// "?..." attached, so it always missed and silently fell back to alert.internal_view).
const splitDashboardPath = (path: string): { pathname: string; search: string } => {
  const queryIndex = path.indexOf("?");
  return queryIndex === -1 ? { pathname: path, search: "" } : { pathname: path.slice(0, queryIndex), search: path.slice(queryIndex) };
};

export const AlertCard = (props: Props) => {
  const { alert } = props;
  const history = useHistory();
  const { dashboards } = useDashboardRegistry();
  const { setCollapsed } = useContext(CedarShellSidenavContext);
  const { mutate: dismiss } = useDismissAlert();

  const dashboardLocation = alert.dashboard_path ? splitDashboardPath(alert.dashboard_path) : null;

  // Only for the "Open {dashboardName}" button below - the actual dashboard this alert routes to,
  // not the alert's own headline (see alert.title just below).
  const dashboardName = dashboards.find((dashboard) => dashboard.path === dashboardLocation?.pathname)?.name ?? alert.internal_view;

  const handleOpen = () => {
    setCollapsed(true);
    history.push(dashboardLocation ?? { pathname: "/dashboards", search: "" });
  };

  return (
    <div className={`alert_card alert_card--${alert.severity}`} data-test-id={`alert-card-${alert.alert_id}`}>
      {/* The card's own headline is alert.title (the real, backend-authored alert name, e.g.
          "Configurator/production variance") - not the destination dashboard's name. Same field
          DashboardAlertWidget.tsx already renders as its own heading for the identical AlertItem
          shape; this was previously showing dashboardName here instead (or its raw internal_view
          slug as a fallback when no dashboard match existed), which is what the alert is ABOUT, not
          what it's actually called. */}
      <h3 className="alert_card_title">{alert.title}</h3>
      <p className="alert_card_triggered">Triggered: {moment(alert.triggered_at).format("MMMM D, YYYY")}</p>
      <p className="alert_card_message">{alert.message}</p>
      <div className="alert_card_actions">
        <button className="alert_card_open" onClick={handleOpen} data-test-id={`alert-card-open-${alert.alert_id}`}>
          Open {dashboardName}
          <ArrowForwardIcon fontSize="small" />
        </button>
        <AlertSnoozeMenu
          alertId={alert.alert_id}
          triggerClassName="alert_card_icon_button"
          triggerContent={<SnoozeIcon fontSize="small" />}
        />
        <button
          className="alert_card_icon_button"
          onClick={() => dismiss(alert.alert_id)}
          aria-label="Dismiss"
          data-test-id={`alert-card-dismiss-${alert.alert_id}`}
        >
          <CloseIcon fontSize="small" />
        </button>
      </div>
    </div>
  );
};
